import {computeAlign, migratePropertyWithinStyleItem, setProperty, deleteProperty} from '../utils'
import type {ComponentProps, SubmenuMode} from '../types'
import type {StyleRef} from '@wix/document-services-types'

const migrateSubmenuItemSpacing = (styleItem: StyleRef) => {
    migratePropertyWithinStyleItem({
        styleItem,
        fromKey: 'dropdown-menu-item-margin-bottom',
        toKey: 'dropdown-menu-item-vertical-spacing'
    })

    migratePropertyWithinStyleItem({
        styleItem,
        fromKey: 'dropdown-menu-sub-item-margin-bottom',
        toKey: 'dropdown-menu-sub-items-vertical-spacing-before'
    })

    setProperty({
        styleItem,
        key: 'dropdown-menu-sub-items-vertical-spacing-between',
        value: styleItem.style.properties['dropdown-menu-sub-items-vertical-spacing-before']
    })
}

const migrateSubmenuItemPadding = (styleItem: StyleRef) => {
    migratePropertyWithinStyleItem({
        styleItem,
        fromKey: 'dropdown-menu-item-padding-top',
        toKey: 'dropdown-menu-item-vertical-padding'
    })

    migratePropertyWithinStyleItem({
        styleItem,
        fromKey: 'dropdown-menu-item-padding-left',
        toKey: 'dropdown-menu-item-horizontal-padding'
    })

    migratePropertyWithinStyleItem({
        styleItem,
        fromKey: 'dropdown-menu-sub-item-padding-top',
        toKey: 'dropdown-menu-sub-item-vertical-padding'
    })

    migratePropertyWithinStyleItem({
        styleItem,
        fromKey: 'dropdown-menu-sub-item-padding-left',
        toKey: 'dropdown-menu-sub-item-horizontal-padding'
    })
}

const migrateSubmenuItemAlign = (styleItem: StyleRef) => {
    const normalizedDropdownMenuItemAlignment = computeAlign(
        styleItem.style.properties['menu-direction'],
        styleItem.style.properties['dropdown-menu-justify-content'],
        styleItem.style.properties['dropdown-menu-item-direction']
    )

    const normalizedDropdownMenuSubItemAlignment = computeAlign(
        styleItem.style.properties['menu-direction'],
        styleItem.style.properties['dropdown-menu-justify-content'],
        styleItem.style.properties['dropdown-menu-sub-item-direction']
    )

    if (normalizedDropdownMenuItemAlignment) {
        setProperty({
            key: 'dropdown-menu-align',
            value: normalizedDropdownMenuItemAlignment,
            styleItem
        })

        setProperty({
            key: 'dropdown-menu-item-align',
            value: normalizedDropdownMenuItemAlignment,
            styleItem
        })

        setProperty({
            key: 'dropdown-menu-sub-item-align',
            value: normalizedDropdownMenuSubItemAlignment,
            styleItem
        })

        deleteProperty({
            key: 'dropdown-menu-justify-content',
            styleItem
        })
    }
}

const migrateSubmenuColumnsLayoutStretching = (styleItem: StyleRef, submenuMode: SubmenuMode) => {
    if (submenuMode === 'columnStretched') {
        setProperty({
            key: 'dropdown-anchor',
            value: 'screen',
            styleItem
        })
    } else {
        setProperty({
            key: 'dropdown-anchor',
            value: 'menuStretched',
            styleItem
        })

        setProperty({
            key: 'dropdown-horizontal-margin',
            value: '0px',
            styleItem
        })
    }
}

const migrateSubmenuColumnsLayoutColumnsSpacing = (styleItem: StyleRef) => {
    migratePropertyWithinStyleItem({
        styleItem,
        fromKey: 'dropdown-container---horizontalSpacing',
        toKey: 'dropdown-menu-item-horizontal-spacing'
    })
}

const migrateSubmenuVerticalDistanceFromMenu = (styleItem: StyleRef) => {
    migratePropertyWithinStyleItem({
        styleItem,
        fromKey: 'dropdown-container-margin-top',
        toKey: 'dropdown-space-above'
    })
}

const migrateSubmenuFlyoutLayout = (styleItem: StyleRef) => {
    setProperty({
        key: 'dropdown-menu-columns-number',
        value: '1',
        styleItem
    })

    setProperty({
        key: 'dropdown-anchor',
        value: 'menuItem',
        styleItem
    })
}

const migrateSubmenuFlyoutLayoutAlignment = (styleItem: StyleRef) => {
    const alignment = styleItem.style.properties['dropdown-container---flyoutAlignment']

    if (!alignment) return

    if (alignment === 'center') {
        setProperty({
            key: 'dropdown-align',
            value: 'center',
            styleItem
        })
    } else {
        setProperty({
            key: 'dropdown-align',
            value: 'start',
            styleItem
        })
    }

    deleteProperty({
        key: 'dropdown-container---flyoutAlignment',
        styleItem
    })
}

const migrateSubmenuContainerPadding = (styleItem: StyleRef) => {
    ;['top', 'right', 'bottom', 'left'].forEach(side => {
        migratePropertyWithinStyleItem({
            styleItem,
            fromKey: `dropdown-container-padding-${side}`,
            toKey: `dropdown-menu-container-padding-${side}`
        })
    })
}

export const migrateSubmenuLayout = (styleItem: StyleRef, componentProps: ComponentProps) => {
    if (componentProps.submenuMode === 'flyout') {
        migrateSubmenuFlyoutLayout(styleItem)
        migrateSubmenuFlyoutLayoutAlignment(styleItem)
    } else {
        migrateSubmenuColumnsLayoutStretching(styleItem, componentProps.submenuMode)
        migrateSubmenuColumnsLayoutColumnsSpacing(styleItem)
    }
    migrateSubmenuVerticalDistanceFromMenu(styleItem)
    migrateSubmenuItemSpacing(styleItem)
    migrateSubmenuItemPadding(styleItem)
    migrateSubmenuItemAlign(styleItem)
    migrateSubmenuContainerPadding(styleItem)
}
